import { useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import Text from '../../../shared/Text';
import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';
import {
  missingCount,
  referencedRecipientFields,
} from '../utils/recipientFields';

import { RecipientFieldDialog } from './RecipientFieldDialog.jsx';

// One line per recipient field referenced in `body`, rendered under the
// email/sms editor, with a "Fill in values" link that opens
// RecipientFieldDialog for that field. A field only matters once its token
// actually appears in the body being composed, so fields not referenced
// there render nothing.
export const RecipientFieldStrip = ({ body }) => {
  const { t } = useTranslation('broadcasts');
  const { userCount, recipientFields: allFields } = useBroadcastContext();
  const [managingField, setManagingField] = useState(null);

  const recipientFields = referencedRecipientFields(allFields, body);

  if (recipientFields.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      {recipientFields.map(field => {
        const missing = missingCount(field.filledCount, userCount);
        const isMissing = missing > 0;

        return (
          <Row key={field.id}>
            <Text variant="small3" color={isMissing ? 'red.100' : 'black.60'}>
              {isMissing
                ? t('recipientFieldStrip.summaryMissing', {
                    name: field.name,
                    filledCount: field.filledCount,
                    recipientCount: userCount,
                    missing,
                  })
                : t('recipientFieldStrip.summary', {
                    name: field.name,
                    filledCount: field.filledCount,
                    recipientCount: userCount,
                  })}
            </Text>
            <ManageLink onClick={() => setManagingField(field)}>
              {t('recipientFieldStrip.fillInValues')}
            </ManageLink>
          </Row>
        );
      })}
      {managingField && (
        <RecipientFieldDialog
          isOpen={!!managingField}
          field={managingField}
          onClose={() => setManagingField(null)}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 8px 20px;
  border-top: 1px solid ${themeGet('colors.black.10')};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ManageLink = styled.button`
  all: unset;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${themeGet('colors.primary.100')};
`;
